import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { IconLock, IconUser, IconArrowRight } from '@tabler/icons-react';
import { useAuth } from '../../contexts/auth/AuthContext';

const Login = () => {
  const { login } = useAuth();
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!credentials.username || !credentials.password) {
        toast.error('Username and password are required');
        return;
      }

      console.log('Sending login request:', {
        username: credentials.username,
        hasPassword: !!credentials.password
      });

      const result = await login(credentials.username, credentials.password);

      if (!result?.success) {
        throw new Error('Login failed');
      }

      console.log('Login successful:', {
        userId: result.user.id,
        username: result.user.username
      });

      toast.success('Login successful!');
    } catch (error) {
      console.error('Error logging in:', {
        message: error.message,
        code: error.code,
        status: error.status,
        data: error.data
      });

      let errorMessage = 'Failed to login. Please try again.';
      if (error.status === 401) {
        errorMessage = 'Invalid username or password';
      } else if (error.status === 400) {
        errorMessage = 'Please provide both username and password';
      } else if (error.status === 500) {
        errorMessage = 'Server error. Please try again later';
      }

      toast.error(errorMessage);
      setCredentials(prev => ({ ...prev, password: '' }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-0 relative overflow-hidden">
      {/* Grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'repeating-conic-gradient(var(--glass-border) 0% 25%, transparent 0% 50%)',
          backgroundSize: '60px 60px'
        }}
      />

      {/* Login card */}
      <div className="ui-motion-enter relative z-10 w-full max-w-sm sm:max-w-md mx-auto px-4 sm:px-6">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-accent mb-4">
            <span className="text-white font-bold text-2xl">B</span>
          </div>
          <h1 className="text-2xl font-display font-black uppercase text-text-primary">Bricktopian</h1>
          <p className="text-text-tertiary mt-1">Creator Platform</p>
        </div>

        {/* Card */}
        <div
          className="p-6 sm:p-8 bg-surface-2 border border-glass-border-hover"
        >
          <h2 className="text-xl font-semibold text-text-primary mb-6">Sign in</h2>

          <form onSubmit={handleSubmit} autoComplete="on" className="space-y-5">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-text-secondary mb-2">
                Username
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <IconUser className="h-4 w-4 text-text-tertiary" />
                </div>
                <input
                  id="username"
                  name="username"
                  type="text"
                  autoComplete="username"
                  required
                  value={credentials.username}
                  onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))}
                  className="block w-full pl-10 pr-4 py-3 glass-input text-sm min-h-[44px]"
                  placeholder="Enter your username"
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-text-secondary mb-2">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                  <IconLock className="h-4 w-4 text-text-tertiary" />
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={credentials.password}
                  onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                  className="block w-full pl-10 pr-4 py-3 glass-input text-sm min-h-[44px]"
                  placeholder="Enter your password"
                  disabled={loading}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`
                w-full flex items-center justify-center gap-2 py-3 px-4 min-h-[44px]
                text-sm font-semibold text-white
                bg-accent
                hover:opacity-90
                focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-surface-1
                ${loading ? 'opacity-50 cursor-not-allowed' : ''}
              `}
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Signing in...
                </>
              ) : (
                <>
                  Sign in
                  <IconArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
